import React from 'react';
import Iconify from 'src/components/Iconify';
import RichEditor from 'src/sections/@dashboard/configuracoes/widget-cms/RichEditor';
import ItensTableEditavel from './ItensTableEditavel';
import { getBlockMeta, isTextoBloco } from './blockTypes';
import { DOCUMENTO_DESIGN as D } from '../EditorDocumento.design';

function formatDataExtenso(date) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date ? new Date(date) : new Date());
}

function addDias(dias) {
  const d = new Date();
  d.setDate(d.getDate() + (Number(dias) || 0));
  return d;
}

// Renderiza um bloco de acordo com seu tipo, tanto em modo edição quanto em leitura.
// `isModelo` = editando um modelo (blocos dinâmicos mostram como serão preenchidos).
// Em edição, os campos são "silenciosos" (sem caixa; borda só no foco) e o texto
// rico usa o tema bubble: o documento lê como documento, não como formulário.
export default function BlocoRenderer({ bloco, proposta, onChange, onChangeProposta, onRemove, onMoveUp, onMoveDown, readOnly, isModelo, isFirst, isLast }) {
  const meta = getBlockMeta(bloco.tipo);
  const contato = proposta.contato || {};

  const renderContent = () => {
    // --- Blocos de texto (rich text) ---
    if (isTextoBloco(bloco.tipo)) {
      if (readOnly) {
        return (
          <div>
            {bloco.titulo && <h3 className="text-base font-bold text-gray-900 dark:text-[#D1D2D3] mb-1">{bloco.titulo}</h3>}
            <div className="text-sm text-gray-700 dark:text-[#D1D2D3] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: bloco.conteudo_html || '' }} />
          </div>
        );
      }
      return (
        <>
          <input
            value={bloco.titulo || ''}
            onChange={e => onChange({ titulo: e.target.value })}
            placeholder="Título da seção (opcional)"
            aria-label="Título da seção"
            className={`${D.docInput} ${D.docSectionTitle}`}
          />
          <RichEditor
            theme="bubble"
            minHeight={56}
            className={D.richBubble}
            value={bloco.conteudo_html}
            onChange={html => onChange({ conteudo_html: html })}
            placeholder={isModelo ? 'Escreva o conteúdo. Use variáveis como {{contato.nome}}. Selecione o texto para formatar.' : 'Escreva o conteúdo desta seção. Selecione o texto para formatar.'}
          />
        </>
      );
    }

    // --- Data ---
    if (bloco.tipo === 'data') {
      const cidade = bloco.config?.cidade || '';
      const dataStr = formatDataExtenso(proposta.created_at);
      if (readOnly) {
        return <p className="text-sm text-gray-600 dark:text-[#ABABAD]">{cidade ? `${cidade}, ` : ''}{dataStr}.</p>;
      }
      return (
        <div className="flex flex-wrap items-baseline gap-1 text-sm text-gray-600">
          <input
            value={cidade}
            onChange={e => onChange({ config: { ...bloco.config, cidade: e.target.value } })}
            placeholder="Cidade (opcional)"
            aria-label="Cidade"
            className={`${D.docInput} w-40 text-sm`}
          />
          <span>{cidade ? ', ' : ''}{dataStr}.</span>
        </div>
      );
    }

    // --- Validade ---
    if (bloco.tipo === 'validade') {
      const dias = proposta.validade_dias || 15;
      const expira = proposta.expira_em ? new Date(proposta.expira_em) : addDias(dias);
      if (readOnly) {
        return <p className="text-sm text-gray-700 dark:text-[#D1D2D3]"><span className="font-semibold">Validade:</span> proposta válida até {formatDataExtenso(expira)}.</p>;
      }
      return (
        <div className="flex flex-wrap items-baseline gap-1.5 text-sm text-gray-600">
          <span className="font-semibold text-gray-700">Validade:</span>
          <span>proposta válida por</span>
          <input
            type="number"
            min={1}
            value={dias}
            onChange={e => onChangeProposta({ validade_dias: Number(e.target.value) })}
            aria-label="Validade em dias"
            className={`${D.docInput} w-12 text-center text-sm tabular-nums`}
          />
          <span>dias {isModelo ? '(a partir do envio)' : `(até ${formatDataExtenso(expira)})`}</span>
        </div>
      );
    }

    // --- Dados do cliente ---
    if (bloco.tipo === 'dados_cliente') {
      if (readOnly) {
        return (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wide mb-1">Em atenção à</p>
            <p className="text-base text-gray-900 dark:text-[#D1D2D3]">{contato.nome || '-'}</p>
            {(contato.email || contato.telefone) && (
              <p className="text-sm text-gray-500 dark:text-[#ABABAD]">{[contato.email, contato.telefone].filter(Boolean).join(' · ')}</p>
            )}
          </div>
        );
      }
      if (isModelo) {
        return (
          <div className="rounded-lg bg-[#F6F5FA] border border-[#E8E5F0] p-3">
            <p className={D.docLabel}>Em atenção à</p>
            <div className="flex flex-wrap gap-1.5">
              {['{{contato.nome}}', '{{contato.email}}', '{{contato.telefone}}'].map(v => (
                <span key={v} className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#F1EAFE] text-[#5B21B6]">{v}</span>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">Preenchido automaticamente com os dados do cliente da oportunidade.</p>
          </div>
        );
      }
      return (
        <div>
          <p className={D.docLabel}>Em atenção à</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
            <input
              value={contato.nome || ''}
              onChange={e => onChangeProposta({ contato: { ...contato, nome: e.target.value } })}
              placeholder="Nome do cliente"
              aria-label="Nome do cliente"
              className={`${D.docInput} text-base`}
            />
            <input
              value={contato.email || ''}
              onChange={e => onChangeProposta({ contato: { ...contato, email: e.target.value } })}
              placeholder="E-mail"
              aria-label="E-mail do cliente"
              className={`${D.docInput} text-sm !text-gray-600`}
            />
            <input
              value={contato.telefone || ''}
              onChange={e => onChangeProposta({ contato: { ...contato, telefone: e.target.value } })}
              placeholder="Telefone"
              aria-label="Telefone do cliente"
              className={`${D.docInput} text-sm !text-gray-600`}
            />
          </div>
        </div>
      );
    }

    // --- Itens do orçamento ---
    if (bloco.tipo === 'itens' || bloco.tipo === 'tabela_precos') {
      return (
        <div>
          {(bloco.titulo || !readOnly) && (
            readOnly ? (
              bloco.titulo && <h3 className="text-base font-bold text-gray-900 dark:text-[#D1D2D3] mb-1">{bloco.titulo}</h3>
            ) : (
              <input
                value={bloco.titulo || ''}
                onChange={e => onChange({ titulo: e.target.value })}
                placeholder="Título da tabela (opcional)"
                aria-label="Título da tabela de itens"
                className={`${D.docInput} ${D.docSectionTitle}`}
              />
            )
          )}
          <ItensTableEditavel
            itens={proposta.itens || []}
            descontoGlobalPercent={proposta.desconto_global_percent}
            onChange={onChangeProposta}
            readOnly={readOnly}
          />
        </div>
      );
    }

    return null;
  };

  const content = renderContent();
  if (content === null) return null;

  if (readOnly) {
    return <div className="mb-5">{content}</div>;
  }

  return (
    <div className={D.blockHover}>
      <div className={D.blockPill}>
        <span className={D.blockPillLabel}>
          <Iconify icon={meta.icon} width={12} />
          {meta.label}
        </span>
        <button type="button" onClick={onMoveUp} disabled={isFirst} className={D.blockPillBtn} title="Mover para cima" aria-label="Mover seção para cima">
          <Iconify icon="mdi:chevron-up" width={14} />
        </button>
        <button type="button" onClick={onMoveDown} disabled={isLast} className={D.blockPillBtn} title="Mover para baixo" aria-label="Mover seção para baixo">
          <Iconify icon="mdi:chevron-down" width={14} />
        </button>
        <button type="button" onClick={onRemove} className={D.blockPillDanger} title="Remover seção" aria-label="Remover seção">
          <Iconify icon="mdi:trash-can-outline" width={14} />
        </button>
      </div>
      {content}
    </div>
  );
}
